/** Preparo do texto para a leitura em voz alta (US-03) — tudo puro.
 *
 * Duas coisas acontecem aqui, e as duas existem por defeito de navegador, não
 * por elegância:
 *
 * 1. **O Chrome trava enunciados longos.** Passar a página inteira num
 *    `SpeechSynthesisUtterance` faz a fala parar no meio, sem erro e sem
 *    `onend` — a leitura simplesmente morre por volta de 15 s. A saída é
 *    fatiar por frase e encadear no `onend`, o que também dá o ponto de parada
 *    que "virar a página" e "sair do livro" precisam.
 *
 * 2. **A camada de texto do pdf.js é fragmentada por posição, não por
 *    sentido.** Cada span é um trecho posicionado, e "fo" + "tossíntese" são
 *    dois spans quando o PDF muda de fonte no meio da palavra. Juntar com
 *    espaço entre TODOS produziria "fo tossíntese"; juntar sem espaço nenhum
 *    colaria palavras vizinhas. Daí a junção depender do que há nas pontas.
 */

/** Tamanho máximo de um pedaço. Acima disso o Chrome trava a fala. */
export const MAX_CARACTERES = 180

/** Junta os pedaços da camada de texto num parágrafo legível.
 *
 * Só insere espaço quando não há um nas pontas e a quebra não parece meio de
 * palavra — separador de sílaba, hífen e abertura de pontuação ficam colados.
 */
export function juntarPedacos(pedacos: readonly string[]): string {
  let texto = ''
  for (const bruto of pedacos) {
    const pedaco = bruto.replace(/\s+/g, ' ')
    if (pedaco.trim() === '') continue
    if (texto === '') {
      texto = pedaco
      continue
    }
    const fim = texto.at(-1) ?? ''
    const inicio = pedaco[0] ?? ''
    const colar = fim === '-' || /\s/.test(fim) || /\s/.test(inicio)
    texto += colar ? pedaco : ` ${pedaco}`
  }
  return texto.replace(/\s+/g, ' ').trim()
}

/** Fatia em pedaços de no máximo `max` caracteres, cortando em fronteira de
 * frase e, na falta dela, entre palavras.
 *
 * Cortar no meio da palavra faria a síntese soletrar o pedaço — e uma frase
 * partida ao meio perde a entonação de fim de frase, que é justamente o que
 * ajuda quem está ouvindo a acompanhar.
 */
export function fatiarEmFrases(texto: string, max: number = MAX_CARACTERES): string[] {
  const limpo = texto.replace(/\s+/g, ' ').trim()
  if (limpo === '') return []

  // A quebra fica DEPOIS da pontuação (lookbehind), para o ponto final entrar
  // no pedaço e a síntese fazer a pausa de fim de frase.
  const frases = limpo.split(/(?<=[.!?…:;])\s+/)
  const pedacos: string[] = []

  for (const frase of frases) {
    if (frase.length <= max) {
      pedacos.push(frase)
      continue
    }
    // Frase longa demais (parágrafo sem pontuação, tabela, lista): quebra entre
    // palavras, nunca no meio de uma.
    let atual = ''
    for (const palavra of frase.split(' ')) {
      if (atual === '') {
        atual = palavra
      } else if (atual.length + 1 + palavra.length <= max) {
        atual += ` ${palavra}`
      } else {
        pedacos.push(atual)
        atual = palavra
      }
    }
    if (atual !== '') pedacos.push(atual)
  }

  return pedacos.filter((p) => p.trim() !== '')
}

/** Lê o texto da CAMADA JÁ RENDERIZADA, não do documento.
 *
 * ⚠️ É de propósito, e é a defesa: a camada só existe para página liberada
 * (o `PDFContentArea` não a monta quando o clipping bloqueia). Ler daqui torna
 * estruturalmente impossível a voz ler capítulo não liberado — enquanto
 * `getTextContent()` entregaria o texto de qualquer página do arquivo.
 */
export function textoDaCamada(camada: HTMLElement | null): string {
  if (!camada) return ''
  const pedacos = Array.from(camada.querySelectorAll('span')).map((s) => s.textContent ?? '')
  return juntarPedacos(pedacos)
}

/** Escolhe a voz em português, preferindo pt-BR.
 *
 * Sem escolha explícita o navegador usa a voz do idioma do SISTEMA: um
 * Chromebook de escola em inglês lê "hereditariedade" com fonética inglesa, o
 * que é pior que não ler.
 */
export function escolherVoz(
  vozes: readonly SpeechSynthesisVoice[],
): SpeechSynthesisVoice | null {
  return (
    vozes.find((v) => v.lang.toLowerCase() === 'pt-br') ??
    vozes.find((v) => v.lang.toLowerCase().startsWith('pt')) ??
    null
  )
}
